import type { UserRole } from '@adweb/shared';

/**
 * Nouveautés du dashboard, de la plus récente à la plus ancienne.
 * Ajouter une entrée en tête à chaque évolution visible, dans la même PR.
 * `platform` : visible seulement des comptes Adweb-Systèmes.
 * Texte livré au navigateur : pas de détail technique ni d'infrastructure.
 */
export interface ChangelogItem {
  text: string;
  audience?: 'all' | 'platform';
}

export interface ChangelogEntry {
  /** Identifiant stable (date ISO + suffixe), sert à savoir ce qui a déjà été vu. */
  id: string;
  date: string;
  title: string;
  items: ChangelogItem[];
}

export const CHANGELOG: ChangelogEntry[] = [
  {
    id: '2026-11-01-menu-reduit',
    date: '2026-11-01',
    title: 'Menu réduit',
    items: [
      {
        text: 'Sur ordinateur, « Réduire le menu » ne garde que les icônes de la barre latérale pour laisser plus de place au contenu. Votre choix est retenu.',
      },
    ],
  },
  {
    id: '2026-11-01-acces-rapide',
    date: '2026-11-01',
    title: 'Accès rapide',
    items: [
      {
        text: 'L’accueil présente un « Accès rapide » : vos automatisations favorites (étoile), ou à défaut les plus utilisées. La liste complète reste dans « Automatisations ».',
      },
      {
        text: 'Vous ne voulez pas de l’accès rapide ? « Masquer » le retire de l’accueil ; il se remet depuis « Affichage ».',
      },
    ],
  },
  {
    id: '2026-11-01-suggestions',
    date: '2026-11-01',
    title: 'Suggestions',
    items: [
      {
        text: 'Nouveau menu « Suggestions » : découvrez les services testés par Adweb-Systèmes et demandez à en profiter en un clic avec « Ça m’intéresse ».',
      },
      {
        text: 'Proposez vos nouveaux services aux clients (toutes les organisations ou certaines), testez-les en brouillon, puis mesurez leur intérêt.',
        audience: 'platform',
      },
    ],
  },
  {
    id: '2026-10-29-echanges',
    date: '2026-10-29',
    title: 'Échanges sur les demandes',
    items: [
      {
        text: 'Chaque demande d’automatisation a désormais son fil d’échanges avec Adweb-Systèmes : posez vos questions et apportez des précisions directement depuis la demande.',
      },
      {
        text: 'Un badge « Nouveau message » signale les réponses non lues ; chaque message est aussi signalé par e-mail et dans la cloche.',
      },
    ],
  },
  {
    id: '2026-10-29',
    date: '2026-10-29',
    title: 'Téléphone, tablette et rapports',
    items: [
      {
        text: 'Le dashboard s’adapte au téléphone et à la tablette : menu repliable, boutons de lancement toujours visibles, tableaux allégés sur petit écran.',
      },
      {
        text: 'Les rapports PDF et CSV de la Synthèse se génèrent de nouveau. Un rapport en échec affiche « Relancer ».',
      },
      {
        text: 'Super-administrateur : case « Coût IA (interne) » pour générer un rapport avec le coût IA, visible de vous seul.',
        audience: 'platform',
      },
    ],
  },
  {
    id: '2026-10-28',
    date: '2026-10-28',
    title: 'Coût IA',
    items: [
      {
        text: 'Super-administrateur : le coût IA remonté par n8n apparaît dans la Synthèse, le journal et le détail des exécutions. Il n’est visible que de vous.',
        audience: 'platform',
      },
      {
        text: 'On peut de nouveau modifier une automatisation en vue Tuiles et depuis sa fiche.',
        audience: 'platform',
      },
    ],
  },
  {
    id: '2026-10-26',
    date: '2026-10-26',
    title: 'Demandes, recherche et favoris',
    items: [
      {
        text: 'Demander une automatisation : décrivez une tâche répétitive, suivez l’avancement de votre demande dans le menu « Demandes ».',
      },
      {
        text: 'Recherche instantanée dans vos automatisations, et étoile pour épingler vos favoris en tête de liste.',
      },
      {
        text: 'Les actions Désactiver, Modifier et Supprimer sont regroupées dans un menu « ⋯ » sur chaque ligne.',
        audience: 'platform',
      },
      { text: 'Le dashboard adopte la marque Adweb-Systèmes.' },
    ],
  },
  {
    id: '2026-10-22',
    date: '2026-10-22',
    title: 'Nouvelle interface',
    items: [
      {
        text: 'Quatre styles au choix dans « Affichage », dont un mode sombre, et des automatisations en liste ou en tuiles (3, 4 ou 5 par ligne).',
      },
      {
        text: 'Un bouton d’action clair pour chaque automatisation : Exécuter, Remplir le formulaire ou Ouvrir le chat.',
      },
      { text: 'Une aide intégrée, avec captures d’écran, accessible depuis le menu « Aide ».' },
      {
        text: 'Filtre par organisation sur la Synthèse, les automatisations et les exécutions.',
        audience: 'platform',
      },
    ],
  },
];

const PLATFORM: ReadonlySet<UserRole> = new Set(['SUPER_ADMIN', 'ADWEB_OPERATOR']);

/** Nouveautés visibles pour un rôle (les entrées sans élément visible sont retirées). */
export function changelogFor(role: UserRole): ChangelogEntry[] {
  return CHANGELOG.map((e) => ({
    ...e,
    items: e.items.filter((i) => i.audience !== 'platform' || PLATFORM.has(role)),
  })).filter((e) => e.items.length > 0);
}
